import {motion} from 'motion/react'
import {ComponentProps} from 'react'
import {IS_TEST} from 'tizra'

interface MotionPropsOptions {
  raiseOnHover?: boolean
}

type MotionProps = Omit<
  ComponentProps<typeof motion.div>,
  keyof ComponentProps<'div'>
>

const whileHover: MotionProps['whileHover'] = {
  y: -4,
  transition: {
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.1,
  },
}

export const getMotionProps = ({
  raiseOnHover = false,
}: MotionPropsOptions = {}): MotionProps => {
  return {
    // If just layout is provided here, then both position and size will be
    // animated. That causes cards to do weird things like grow from
    // zero-height.
    layout: 'position',

    // When running jsdom tests, don't animate on enter, otherwise we get
    // a random opacity in the snapshot depending on the timing.
    initial: {opacity: IS_TEST ? 1 : 0},

    animate: {
      opacity: 1,
      // Using easeIn here, along with the delay, allows exit animations to
      // mostly finish and layout animation to be underway before fading in the
      // new items. Unfortunately, it also imposes a slight delay on the initial
      // entrance, but I haven't figured out a way around that.
      transition: {
        type: 'tween',
        ease: 'easeIn',
        delay: 0.2,
        duration: 0.3,
      },
    },

    exit: {
      opacity: 0,
      // "I usually use the "easeOut" curve for enter and exit transitions. The
      // acceleration at the beginning gives the user a feeling of
      // responsiveness. I use a duration no longer than
      // 0.3/0.4 seconds to keep the animation fast."
      // ~ Emil Kowalski, Animations on the Web https://animations.dev/
      transition: {type: 'tween', ease: 'easeOut', duration: 0.3},
    },

    transition: {
      // Originally used type: 'spring' here, but it feels uncomfortable when
      // the cards infringe on the filters during the transition.
      layout: {type: 'tween', ease: 'easeOut', duration: 0.4},
    },

    ...(raiseOnHover && {whileHover}),
  }
}
